
import type { Period } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { DateRange } from '../../models/domain/date-range'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import { DateUtils } from '../../utils/date.utils'
import { UiPopover } from '../keen-ui'
import { EpbBaseInput } from '../shared/epb-base-input'
import { EpbButton } from '../shared/epb-button'
import { EpbInputHelp } from '../shared/epb-input-help'
import DateRangePicker from './date-range-picker.component.vue'

export default defineComponent({
  name: 'DateRangePickerSelector',
  components: {
    DateRangePicker,
    EpbBaseInput,
    EpbButton,
    EpbInputHelp,
    UiPopover,
  },
  props: {
    value: {
      type: Object as PropType<Period<Date>>,
      default: () => ({ start: undefined, end: undefined }),
    },
    label: {
      type: String,
      required: true,
    },
    allowFutureSelection: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const baseClass = 'epb-date-range-picker-selector'
    const messages = ctx.proxy.$epb.messages.filters.customDate

    const popover = ref()
    const isPopoverOpen = ref(false)

    const dateRange = ref<DateRange>({
      startDate: undefined,
      endDate: undefined,
    })

    const hasDateRange = computed(() => {
      return !!dateRange.value.startDate
    })

    const hasError = computed(() => {
      const startDate = dateRange.value.startDate
      const endDate = dateRange.value.endDate
      return !!(startDate && !endDate)
    })

    const selectedValuesText = computed(() => {
      const startDate = dateRange.value.startDate
      const endDate = dateRange.value.endDate
      const locale = ctx.proxy.$epb.locale

      if (startDate && endDate) {
        if (DateUtils.areDatesEqual(startDate, endDate)) {
          return DateUtils.formatDateAsDay(startDate, locale)
        }
        return `${DateUtils.formatDateAsDay(startDate, locale)} - ${DateUtils.formatDateAsDay(endDate, locale)}`
      }

      if (startDate) {
        return DateUtils.formatDateAsDay(startDate, locale)
      }

      return undefined
    })

    function closePopover(): void {
      if (popover.value) {
        popover.value.close()
      }
    }

    function onDateRangePickerChange(newDateRange: DateRange): void {
      dateRange.value = newDateRange
      if (newDateRange.startDate && newDateRange.endDate) {
        const period = {
          start: new Date(newDateRange.startDate),
          end: new Date(newDateRange.endDate),
        }
        const adjustedPeriod = DateUtils.getPeriodWithoutHours(period)

        emit('change', adjustedPeriod)
        closePopover()
      }
    }

    function clearDateRange(): void {
      dateRange.value = {
        startDate: undefined,
        endDate: undefined,
      }
      emit('change', [undefined, undefined])
    }

    watch(
      () => props.value,
      newValue => {
        dateRange.value = {
          startDate: newValue.start ?? undefined,
          endDate: newValue.end ?? undefined,
        }
      },
      { deep: true, immediate: true },
    )

    return {
      baseClass,
      messages,
      popover,
      isPopoverOpen,
      dateRange,
      hasDateRange,
      hasError,
      selectedValuesText,
      onDateRangePickerChange,
      clearDateRange,
    }
  },
})
